import { RequisitionDecision, RequisitionStatus } from '@/enums/materialsRequisition'
import { useFetchStore } from '@/stores/fetch'
import type { TagItem } from '@/types/Common'
import type {
    IMaterialsApprovalRule,
    IMaterialsApprovalRuleRequest,
    IMaterialsRequisition,
    IMaterialsRequisitionApproval,
    IMaterialsRequisitionOrderResult,
    IMaterialsRequisitionRequest,
} from '@/types/MaterialsRequisition'

/**
 * The asking half of Imprion Materials, from the browser's side
 * (docs/MATERIALNO-ZAHTEVKI.md, WP-M4).
 *
 * The calls live here rather than in the five views for the reason `libs/stockOrder.ts`
 * holds the buying half's: the list, the detail and „Moje odobritve“ all read a
 * requisition and all act on it, and three copies of one URL would eventually be three
 * URLs.
 */

/** Every route of this domain hangs off one property. */
const requisitionBase = (propertyId: string): string => `/properties/${propertyId}/materials/requisitions`
const approvalsBase   = (propertyId: string): string => `/properties/${propertyId}/materials/approvals`
const rulesBase       = (propertyId: string): string => `/properties/${propertyId}/materials/approval-rules`

/**
 * The "none" row of a select.
 *
 * A Nuxt UI select refuses an item whose value is the empty string — Reka's
 * `<SelectItem>` keeps '' for "clear" — so every optional choice on this domain's forms
 * is drawn as this word and stored as ''. The same three lines as `libs/stockOrder.ts`,
 * `libs/stockInvoice.ts` and `libs/stockReceiving.ts` carry, and copied for the reason
 * each of those gives: it is three lines rather than an import that ties two domains
 * together.
 */
export const NONE_CHOICE = 'none'
export const choiceOf = (value: string | undefined | null): string => value || NONE_CHOICE
export const valueOf = (choice: string | undefined | null): string => choice === NONE_CHOICE ? '' : (choice ?? '')

/** A day that was never set comes back as year one; nothing is drawn for it. */
export const isSet = (value?: string | null): boolean => !!value && !String(value).startsWith('0001')

/** The colour each rung of the requisition's ladder is drawn in. */
export const requisitionStatusColor = (status: string): TagItem['color'] => {
    const map: Record<string, TagItem['color']> = {
        [RequisitionStatus.Draft]:     'neutral',
        [RequisitionStatus.Submitted]: 'warning',
        [RequisitionStatus.Approved]:  'success',
        [RequisitionStatus.Rejected]:  'error',
        [RequisitionStatus.Ordered]:   'primary',
        [RequisitionStatus.Cancelled]: 'neutral',
    }

    return map[status] ?? 'neutral'
}

/** The same for one approver's answer on one rung. */
export const decisionColor = (decision: string): TagItem['color'] => {
    const map: Record<string, TagItem['color']> = {
        [RequisitionDecision.Pending]:  'warning',
        [RequisitionDecision.Approved]: 'success',
        [RequisitionDecision.Rejected]: 'error',
    }

    return map[decision] ?? 'neutral'
}

/** The rung the requisition is standing on, or null when it is standing on none. */
export const currentApproval = (
    requisition: IMaterialsRequisition | null,
): IMaterialsRequisitionApproval | null => {
    if (!requisition) return null

    return (requisition.approvals ?? []).find(
        approval => approval.level === requisition.current_level && approval.decision === RequisitionDecision.Pending,
    ) ?? null
}

/** Whether the person on the current rung has run out of time. */
export const isOverdue = (dueAt?: string | null): boolean =>
    isSet(dueAt) && new Date(String(dueAt)).getTime() < Date.now()

/** The requisition's own due date: the one the rung it is standing on carries. */
export const overdueRequisition = (requisition: IMaterialsRequisition): boolean =>
    isOverdue(currentApproval(requisition)?.due_at)

/**
 * Which acts a screen may offer on a requisition.
 *
 * It is HERE and not in a template, because the same question is asked by the list, by
 * the detail and by „Moje odobritve“ — and because it is the browser's half of a rule
 * the server enforces: a button offered from the wrong state is a refusal a person
 * meets after pressing, which is exactly what the server's own sentence then has to
 * explain.
 *
 * The rights are checked beside it, and asymmetrically on purpose. Writing, submitting
 * and withdrawing are the REQUESTER's own acts and belong to whoever raised the paper;
 * approving and rejecting are offered to anybody holding `materials:approve`, because
 * only the server knows whose turn it actually is — it holds the ladder — and a screen
 * that guessed would hide the button from the deputy the escalation just let in.
 */
export const requisitionActions = (
    requisition: IMaterialsRequisition | null,
    rights: { mayRequisition: boolean, mayApprove: boolean, mayOrder: boolean, isRequester: boolean },
): string[] => {
    if (!requisition) return []

    const own = rights.mayRequisition && rights.isRequester

    switch (requisition.status) {
        case RequisitionStatus.Draft:
            return own ? ['submit', 'edit', 'cancel'] : []
        case RequisitionStatus.Submitted:
            return [
                ...(rights.mayApprove ? ['approve', 'reject'] : []),
                ...(own ? ['cancel'] : []),
            ]

        // The paper is signed and the buyer turns it into orders. Cancelling is still
        // open until it has been: an approved requisition nobody ordered against is a
        // decision the house is allowed to change its mind about.
        case RequisitionStatus.Approved:
            return [
                ...(rights.mayOrder ? ['order'] : []),
                ...(own ? ['cancel'] : []),
            ]
        default:
            return []
    }
}

/** The requisitions, optionally by where each stands and optionally only one's own. */
export const listRequisitions = async (
    propertyId: string,
    status = '',
    mine = false,
): Promise<{ ok: boolean, requisitions: IMaterialsRequisition[], body: unknown }> => {
    const query = new URLSearchParams()

    if (status) query.set('status', status)
    if (mine) query.set('mine', '1')

    const search = query.toString()
    const suffix = search ? `?${search}` : ''

    const { statusCode, data } = await useFetchStore()
        .fetch(`${requisitionBase(propertyId)}${suffix}`)
        .get()
        .json<IMaterialsRequisition[]>()

    return { ok: statusCode.value === 200, requisitions: data.value ?? [], body: data.value }
}

/** One requisition, with its lines and the whole of its ladder. */
export const getRequisition = async (
    propertyId: string,
    requisitionId: string,
): Promise<{ ok: boolean, requisition: IMaterialsRequisition | null, body: unknown }> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`${requisitionBase(propertyId)}/${requisitionId}`)
        .get()
        .json<IMaterialsRequisition>()

    return { ok: statusCode.value === 200, requisition: data.value ?? null, body: data.value }
}

/** A new requisition, as a draft. Nothing is asked of anybody until it is submitted. */
export const createRequisition = async (
    propertyId: string,
    payload: IMaterialsRequisitionRequest,
): Promise<{ ok: boolean, status: number, id: string, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(requisitionBase(propertyId))
        .post(payload)
        .json<{ id: string }>()

    return {
        ok: response.value?.status === 201,
        // Carried out to the dialog so that a refusal can be ACTED on and not only
        // printed: the rescale guard answers 409, and the dialog reloads its articles.
        status: response.value?.status ?? 0,
        id: data.value?.id ?? '',
        body: data.value,
    }
}

/** A draft corrected. The server refuses every other state, and so do the screens. */
export const updateRequisition = async (
    propertyId: string,
    requisitionId: string,
    payload: IMaterialsRequisitionRequest,
): Promise<{ ok: boolean, status: number, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`${requisitionBase(propertyId)}/${requisitionId}`)
        .put(payload)
        .json()

    return { ok: response.value?.status === 200, status: response.value?.status ?? 0, body: data.value }
}

/**
 * One of the four moves.
 *
 * One function for all of them because they are one shape — a POST to a named door with
 * an optional body — and because the ORDER is the only one that answers anything, which
 * the caller reads out of `body`.
 *
 * Three of the four take an `operation_id` in that body (approve, reject, cancel), and
 * since 2026-09-11 so does the ORDER — for a different reason. The three decisions are
 * idempotent through the requisition's own state machine and the name only turns a
 * second press from refused into answered; a conversion WRITES SEVERAL DOCUMENTS, and
 * without a name the retry of one that broke half-way would be a fresh act. The screen
 * mints the name once per press and keeps it across a failure, which is what makes a
 * retry a retry rather than a second conversion
 * (docs/MATERIALNO-ZAHTEVKI.md §5, §9.1).
 */
export const moveRequisition = async (
    propertyId: string,
    requisitionId: string,
    action: string,
    payload: object = {},
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`${requisitionBase(propertyId)}/${requisitionId}/${action}`)
        .post(payload)
        .json<IMaterialsRequisitionOrderResult>()

    return { ok: response.value?.status === 200 || response.value?.status === 201, body: data.value }
}

/** What is waiting for THIS account to answer, in the order the server puts it. */
export const listMyApprovals = async (
    propertyId: string,
): Promise<{ ok: boolean, requisitions: IMaterialsRequisition[], body: unknown }> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`${approvalsBase(propertyId)}/mine`)
        .get()
        .json<IMaterialsRequisition[]>()

    return { ok: statusCode.value === 200, requisitions: data.value ?? [], body: data.value }
}

/** The house's approval ladder, as rules. */
export const listApprovalRules = async (
    propertyId: string,
): Promise<{ ok: boolean, rules: IMaterialsApprovalRule[], body: unknown }> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(rulesBase(propertyId))
        .get()
        .json<IMaterialsApprovalRule[]>()

    return { ok: statusCode.value === 200, rules: data.value ?? [], body: data.value }
}

/** One new rule. */
export const createApprovalRule = async (
    propertyId: string,
    payload: IMaterialsApprovalRuleRequest,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(rulesBase(propertyId))
        .post(payload)
        .json<{ id: string }>()

    return { ok: response.value?.status === 201 || response.value?.status === 200, body: data.value }
}

/** One rule corrected. */
export const updateApprovalRule = async (
    propertyId: string,
    ruleId: string,
    payload: IMaterialsApprovalRuleRequest,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`${rulesBase(propertyId)}/${ruleId}`)
        .put(payload)
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/** One rule withdrawn. The DELETE answers 200 here and 204 elsewhere (CLAUDE.md, gap 5). */
export const deleteApprovalRule = async (
    propertyId: string,
    ruleId: string,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`${rulesBase(propertyId)}/${ruleId}`)
        .delete()
        .json()

    return { ok: response.value?.status === 200 || response.value?.status === 204, body: data.value }
}
